import React, { useContext, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useMutation, useQueryClient } from "react-query"
import { mutationRequest } from "../../../util/request"
import { UserContext } from "../../HomeApp"
import { InterestedButton } from "../../shared/Button"
import { useReactions } from "../../../hooks/useReactions"
import Pluralize from "react-pluralize"
import { ActionCableConsumer } from "react-actioncable-provider"
import { openAuthDialog } from "../../Authentication"
import styles from "./styles.module.scss"

type RenderMode = "full" | "text" | "minimal"

interface Props {
  postId: number
  maxRsvps: number | null
  renderMode?: RenderMode
}

interface ReceivedReaction {
  reaction?: {
    interested_count?: number
  }
}

const LOW_RSVP_THRESHOLD = 5

const InterestedBlock = ({ postId, maxRsvps, renderMode = "full" }: Props) => {
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const [interestedPeople, setInterestedPeople] = useState(0)
  const [userInterestedState, setUserInterestedState] = useState("")
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [rsvpsRemaining, setRsvpsRemaining] = useState(maxRsvps == 0 ? 1000 : 0)

  const { isLoading, isError, data, status } = useReactions(
    "Post",
    postId,
    "rsvps",
    true,
  )

  useEffect(() => {
    switch (true) {
      case maxRsvps === null:
        setRsvpsRemaining(10000)
        break
      case maxRsvps > 0:
        setRsvpsRemaining(maxRsvps - interestedPeople)
        break
      default:
        setRsvpsRemaining(0)
        break
    }
  }, [interestedPeople])

  useEffect(() => {
    if (!isLoading && !isError && status === "success") {
      setInterestedPeople(data.total_count)
      if (user) {
        if (data?.current_user_setting?.reaction_type)
          setUserInterestedState(data.current_user_setting.reaction_type)
        else {
          setUserInterestedState("uninterested")
        }
      } else {
        setUserInterestedState("unauthenticated")
      }
    }
  }, [isError, isLoading, status, data?.total_count])

  const mutation = useMutation(mutationRequest)

  const submitReaction = async (
    e: React.MouseEvent<HTMLButtonElement>,
    reactionState: string,
  ) => {
    setButtonDisabled(true)
    e.preventDefault()
    await mutation.mutate({
      options: { method: "POST" },
      path: `/api/v1/reactions`,
      data: {
        reaction: {
          content_type: "Post",
          content_id: postId,
          post_id: postId,
          reaction_type: reactionState,
        },
        filter: "rsvps",
      },
    })
  }

  const handleReceivedMessage = (newReaction: ReceivedReaction) => {
    if (newReaction.reaction && newReaction.reaction.interested_count) {
      setInterestedPeople(newReaction.reaction.interested_count)
    }
  }

  const queryClient = useQueryClient()

  useEffect(() => {
    if (mutation.isSuccess) {
      setUserInterestedState(
        mutation.data?.current_user_setting?.reaction_type || "uninterested",
      )
      setButtonDisabled(false)
      setInterestedPeople(mutation.data.total_count)
      queryClient.invalidateQueries("user-posts")
    }
  }, [mutation.isSuccess, mutation.isError])

  return (
    <>
      <ActionCableConsumer
        channel={{ channel: "PostChannel", id: postId }}
        onReceived={handleReceivedMessage}
      >
        {rsvpsRemaining === 0 && userInterestedState !== "interested" ? (
          <>
            <div className="mb-4">
              <span role="img" aria-label="sad face">
                😞
              </span>{" "}
              <span className={styles.fullCapacity}>
                Sorry, it's maxed out. Check back in case if spots open up.
              </span>
            </div>
          </>
        ) : (
          <div className={`flex ${renderMode === "text" ? "" : "my-6"}`}>
            {user ? (
              renderMode === "text" ? (
                <button
                  className="mx-3 focus:outline-none text-blue-600"
                  disabled={buttonDisabled}
                  onClick={(e) =>
                    submitReaction(
                      e,
                      userInterestedState === "interested"
                        ? "uninterested"
                        : "interested",
                    )
                  }
                >
                  {userInterestedState === "interested"
                    ? "You are Going!"
                    : "RSVP?"}
                </button>
              ) : (
                <InterestedButton
                  disabled={buttonDisabled}
                  renderMode={renderMode}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                    submitReaction(
                      e,
                      userInterestedState === "interested"
                        ? "remove"
                        : "interested",
                    )
                  }
                  bg={
                    userInterestedState === "interested"
                      ? "bg-black-800"
                      : "bg-green-700"
                  }
                >
                  {userInterestedState === "interested"
                    ? "You are Going!"
                    : "RSVP?"}
                </InterestedButton>
              )
            ) : renderMode === "text" ? (
              <button
                className="mx-3 focus:outline-none text-blue-600"
                onClick={(e) =>
                  dispatch(
                    openAuthDialog({
                      explanation:
                        "You must first sign up or log in to respond to an event",
                    }),
                  )
                }
              >
                RSVP?
              </button>
            ) : (
              <InterestedButton
                disabled={buttonDisabled}
                renderMode={renderMode}
                onClick={() =>
                  dispatch(
                    openAuthDialog({
                      explanation:
                        "You must first sign up or log in to respond to an event",
                    }),
                  )
                }
              >
                RSVP?
              </InterestedButton>
            )}
            {interestedPeople > 0 &&
              renderMode !== "text" &&
              rsvpsRemaining < LOW_RSVP_THRESHOLD && (
                <div
                  className={`flex-grow ${
                    renderMode === "minimal" ? "" : "text-sm"
                  } ml-2`}
                >
                  <span>
                    {rsvpsRemaining == 0 ? "" : "only"}{" "}
                    <span className={styles.lowCapacity}>
                      <Pluralize
                        singular={"spot"}
                        plural={"spots"}
                        count={rsvpsRemaining}
                        className="font-bold"
                      />{" "}
                    </span>
                    left
                  </span>
                </div>
              )}
          </div>
        )}
      </ActionCableConsumer>
    </>
  )
}

export default InterestedBlock
